/* Appsalon: hide/block Swish Wishlist add-to-cart buttons below configured inventory threshold. */
(function () {
  'use strict';

  const SECTION_ID = 'appsalon-inventory-threshold-data';
  const BLOCKED_CLASS = 'inventory-threshold-blocked';
  const CARD_SELECTOR = 'wishlist-product-card, .wk-product-card';
  const FORM_SELECTOR = 'wishlist-product-card form[action*="/cart/add"], form.wk-form[action*="/cart/add"], .wk-product-card form[action*="/cart/add"]';
  const BUTTON_SELECTOR = '.wk-cta-button';
  const productCache = new Map();
  let scheduled = false;

  const getThreshold = () => {
    const threshold = parseInt(window.KROWN?.settings?.inventory_purchase_threshold, 10) || 0;
    return threshold > 0 ? threshold : 0;
  };

  const normalizeVariantId = (value) => {
    if (value === null || typeof value === 'undefined') return '';
    return String(value).trim();
  };

  const isVariantBlocked = (variant) => {
    if (!variant || getThreshold() <= 0) return false;

    if (window.KROWN?.functions?.variantBlockedByInventoryThreshold) {
      return window.KROWN.functions.variantBlockedByInventoryThreshold(variant);
    }

    const hasManagedInventory = variant.inventory_management !== null &&
      typeof variant.inventory_management !== 'undefined' &&
      variant.inventory_management !== '';

    if (!hasManagedInventory) return false;

    const quantity = Number(
      typeof variant.inventory_quantity !== 'undefined'
        ? variant.inventory_quantity
        : variant.quantity
    );

    return !Number.isNaN(quantity) && quantity < getThreshold();
  };

  const extractProductPath = (element) => {
    const card = element.closest(CARD_SELECTOR) || element;
    const productLink = card.querySelector('a[href*="/products/"]') || element.querySelector?.('a[href*="/products/"]');

    if (!productLink) return '';

    try {
      const url = new URL(productLink.getAttribute('href'), window.location.origin);
      return url.pathname;
    } catch (error) {
      return '';
    }
  };

  const extractVariantId = (form) => {
    const input = form.querySelector('input[name="id"]');
    if (input?.value) return normalizeVariantId(input.value);

    const card = form.closest(CARD_SELECTOR);
    const link = card?.querySelector('a[href*="/products/"][href*="variant="]');
    if (!link) return '';

    try {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      return normalizeVariantId(url.searchParams.get('variant'));
    } catch (error) {
      return '';
    }
  };

  const sectionUrl = (productPath, parameter) => {
    const separator = productPath.includes('?') ? '&' : '?';
    return `${productPath}${separator}${parameter}=${encodeURIComponent(SECTION_ID)}`;
  };

  const parseInventoryHtml = (html) => {
    if (!html) return null;

    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const dataScript = documentFragment.querySelector('[data-appsalon-product-inventory-threshold]');
    if (!dataScript) return null;

    const productData = JSON.parse(dataScript.textContent || '{}');
    const variantsById = new Map();

    (productData.variants || []).forEach((variant) => {
      variantsById.set(normalizeVariantId(variant.id), variant);
    });

    return variantsById;
  };

  const fetchInventoryData = (productPath) => {
    if (!productPath) return Promise.resolve(null);
    if (productCache.has(productPath)) return productCache.get(productPath);

    const request = fetch(sectionUrl(productPath, 'sections'), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Inventory threshold section request failed: ${response.status}`);
        return response.json();
      })
      .then((sections) => parseInventoryHtml(sections && sections[SECTION_ID]))
      .then((variantsById) => {
        if (variantsById) return variantsById;

        return fetch(sectionUrl(productPath, 'section_id'), {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        })
          .then((response) => response.ok ? response.text() : '')
          .then(parseInventoryHtml);
      })
      .catch((error) => {
        console.warn('Could not load Appsalon inventory threshold data for wishlist product', error);
        return null;
      });

    productCache.set(productPath, request);
    return request;
  };

  const setBlockedState = (form, blocked) => {
    const card = form.closest(CARD_SELECTOR);
    const button = form.querySelector(BUTTON_SELECTOR);

    // Keep the wishlist product card visible. Only the cart form/button should be
    // blocked/hidden when inventory is below the theme threshold.
    card?.classList.toggle(BLOCKED_CLASS, blocked);

    [form, button].forEach((element) => {
      if (!element) return;
      element.classList.toggle(BLOCKED_CLASS, blocked);
      if (blocked) {
        element.setAttribute('data-inventory-threshold-blocked', 'true');
      } else {
        element.removeAttribute('data-inventory-threshold-blocked');
      }
    });

    // Safety cleanup for cards that may have been marked by an older version of
    // this file. The card must never get a hidden/blocking data attribute.
    card?.removeAttribute('data-inventory-threshold-blocked');

    if (button) {
      button.hidden = blocked;
      button.disabled = blocked;
      if (blocked) {
        button.setAttribute('aria-hidden', 'true');
        button.setAttribute('tabindex', '-1');
      } else {
        button.removeAttribute('aria-hidden');
        button.removeAttribute('tabindex');
      }
    }
  };

  const processForm = async (form) => {
    if (!form || form.dataset.appsalonWishlistInventoryProcessing === 'true') return;
    if (getThreshold() <= 0) {
      setBlockedState(form, false);
      return;
    }

    const variantId = extractVariantId(form);
    const productPath = extractProductPath(form);

    if (!variantId || !productPath) return;

    form.dataset.appsalonWishlistInventoryProcessing = 'true';

    try {
      const variantsById = await fetchInventoryData(productPath);
      const variant = variantsById?.get(variantId);
      const canPurchase = window.KROWN?.functions?.variantCanPurchase
        ? window.KROWN.functions.variantCanPurchase(variant)
        : Boolean(variant?.available) && !isVariantBlocked(variant);

      setBlockedState(form, variant ? !canPurchase : false);
    } finally {
      form.dataset.appsalonWishlistInventoryProcessing = 'false';
    }
  };

  const processWishlistButtons = () => {
    if (getThreshold() <= 0) return;
    document.querySelectorAll(FORM_SELECTOR).forEach((form) => {
      processForm(form);
    });
  };

  const scheduleProcess = () => {
    if (scheduled) return;
    scheduled = true;
    window.setTimeout(() => {
      scheduled = false;
      processWishlistButtons();
    }, 100);
  };

  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.(FORM_SELECTOR);
    if (!form || !form.classList.contains(BLOCKED_CLASS)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.(BUTTON_SELECTOR);
    if (!button) return;

    const form = button.closest(FORM_SELECTOR);
    if (!form || !form.classList.contains(BLOCKED_CLASS)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleProcess, { once: true });
  } else {
    scheduleProcess();
  }

  window.addEventListener('load', scheduleProcess);
  window.addEventListener('pageshow', scheduleProcess);
  document.addEventListener('shopify:section:load', scheduleProcess);
  document.addEventListener('wk:loaded', scheduleProcess);
  document.addEventListener('wishlist:loaded', scheduleProcess);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList' && (mutation.addedNodes.length || mutation.removedNodes.length)) {
        scheduleProcess();
        return;
      }
      if (mutation.type === 'attributes') {
        scheduleProcess();
        return;
      }
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['href', 'value', 'data-variant-id', 'data-wishlist-item-id']
  });

  window.setTimeout(scheduleProcess, 1000);
  window.setTimeout(scheduleProcess, 3000);
  window.setTimeout(scheduleProcess, 6000);
}());
